import React, { useEffect, useMemo, useRef, useState } from 'react';

// ─── Veg / Non-Veg dots ───────────────────────────────────────────────────────
const VEG_DOT = (
  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, border: '1.5px solid #16a34a', borderRadius: 3, flexShrink: 0 }}>
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a' }} />
  </span>
);
const NV_DOT = (
  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, border: '1.5px solid #dc2626', borderRadius: 3, flexShrink: 0 }}>
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#dc2626' }} />
  </span>
);

function foodBadge(item) {
  const ft = String(item.food_type || item.food_type_name || '').toLowerCase();
  if (ft.includes('non') || ft.includes('nv') || ft === 'non-veg') return NV_DOT;
  if (ft.includes('veg') || ft === 'v') return VEG_DOT;
  if (item.is_veg === true) return VEG_DOT;
  if (item.is_veg === false) return NV_DOT;
  return null;
}

// ─── Category icon mapping ─────────────────────────────────────────────────────
const CAT_MAP = [
  [['appetizer', 'starter'], '🥗'],
  [['soup'], '🥣'],
  [['main', 'curry', 'gravy', 'entree'], '🍛'],
  [['biryani', 'rice', 'pulao', 'fried rice'], '🍚'],
  [['dal', 'lentil', 'daal'], '🍲'],
  [['bread', 'roti', 'naan', 'paratha', 'kulcha', 'puri'], '🫓'],
  [['dessert', 'sweet', 'mithai', 'pudding'], '🍮'],
  [['ice cream', 'kulfi', 'gelato'], '🍦'],
  [['beverage', 'drink', 'juice', 'mocktail', 'cocktail', 'shake', 'smoothie', 'lassi'], '🥤'],
  [['coffee', 'espresso', 'cappuccino', 'latte'], '☕'],
  [['tea', 'chai'], '🍵'],
  [['snack', 'finger', 'tikka', 'kebab', 'fry', 'chaat'], '🍢'],
  [['pizza'], '🍕'],
  [['pasta', 'noodle', 'spaghetti'], '🍝'],
  [['burger', 'sandwich', 'wrap', 'roll', 'sub'], '🍔'],
  [['seafood', 'prawn', 'fish', 'crab', 'lobster', 'shrimp'], '🦐'],
  [['chicken', 'poultry'], '🍗'],
  [['mutton', 'lamb', 'beef', 'pork', 'meat'], '🥩'],
  [['side', 'raita', 'chutney', 'pickle', 'accompaniment'], '🥘'],
  [['salad'], '🥗'],
];

function catIcon(cat) {
  if (!cat || cat === 'ALL') return '🍽️';
  const l = cat.toLowerCase();
  for (const [keys, icon] of CAT_MAP) {
    if (keys.some(k => l.includes(k))) return icon;
  }
  return '🍴';
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
function humanDur(openedAt) {
  if (!openedAt) return null;
  const mins = Math.floor((Date.now() - new Date(openedAt).getTime()) / 60000);
  if (mins < 1) return '< 1m';
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) { const h = Math.floor(mins / 60); const m = mins % 60; return m > 0 ? `${h}h ${m}m` : `${h}h`; }
  const days = Math.floor(mins / 1440);
  const hrs = Math.floor((mins % 1440) / 60);
  // Sessions open for days are stale — flag them visually
  return hrs > 0 ? `${days}d ${hrs}h` : `${days}d`;
}

function relTime(ts) {
  if (!ts) return '';
  const t = new Date(ts).getTime();
  if (!Number.isFinite(t)) return '';
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `${hrs}h ${mins % 60}m ago` : new Date(t).toLocaleDateString();
}

const ORDER_STATUS = {
  OPEN:        ['#dbeafe', '#1d4ed8', 'Open'],
  PARTIAL_KOT: ['#fef3c7', '#92400e', 'Partial KOT'],
  KOT_SENT:    ['#ede9fe', '#6d28d9', 'KOT Sent'],
  PREPARING:   ['#fff7ed', '#c2410c', 'Preparing'],
  READY:       ['#dcfce7', '#15803d', 'Ready'],
  CLOSED:      ['#f1f5f9', '#475569', 'Closed'],
  VOID:        ['#fee2e2', '#b91c1c', 'Void'],
};

// ─── Main Component ────────────────────────────────────────────────────────────
export default function MenuAndOrder({
  menuItems,
  cartItems,
  itemQuantities,
  tableInfo,
  roomInfo,
  serviceType,
  onAddToCart,
  onUpdateCartQty,
  onRemoveFromCart,
  onUpdateNote,
  onUpdatePreference,
  onPlaceOrder,
  onBack,
  isMobile,
  placing,
  currencySymbol = '₹',
  taxPercent,
  cartSubtotal,
  cartTaxAmount,
  cartTotal,
  existingOrders = [],
  sessionInfo = null,
  fetchOrderItems = null,
  tables = [],
  onGenerateBill = null,
  onTableTransfer = null,
  onExit = null,
  onClearSession = null,
  discountAmount = 0,
  discountType = 'PERCENT',
  discountValue = '',
  onDiscountTypeChange = null,
  onDiscountValueChange = null,
  onClearCart = null,
  onSaveOrder = null,
  savingOrder = false,
  onPrintLatestKot = null,
  onGuestDetails = null,
}) {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [dietFilter, setDietFilter] = useState('ALL');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [orderItemsById, setOrderItemsById] = useState({});
  const [showTransferPicker, setShowTransferPicker] = useState(false);
  const [transferTarget, setTransferTarget] = useState('');
  const [showOfferEditor, setShowOfferEditor] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('kot_menu_favorites') || '[]')); }
    catch { return new Set(); }
  });
  const toggleFavorite = (itemId) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
      try { localStorage.setItem('kot_menu_favorites', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    if (!fetchOrderItems || !existingOrders.length) return;
    let cancelled = false;
    (async () => {
      const ids = existingOrders.map(o => o.id).filter(Boolean);
      const missing = ids.filter(id => !orderItemsById[id]);
      for (const id of missing) {
        try {
          const items = await fetchOrderItems(id);
          if (cancelled) return;
          setOrderItemsById(prev => ({ ...prev, [id]: Array.isArray(items) ? items : [] }));
        } catch {
          if (!cancelled) setOrderItemsById(prev => ({ ...prev, [id]: [] }));
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingOrders.map(o => o.id).join(','), fetchOrderItems]);

  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  const categories = useMemo(() => {
    const cats = [...new Set(menuItems.map(i => String(i.category_name || i.menu_type_name || '').trim() || 'General'))].sort();
    return ['ALL', ...cats];
  }, [menuItems]);

  const filtered = useMemo(() => {
    let items = menuItems;
    if (selectedCategory !== 'ALL') {
      items = items.filter(i => (String(i.category_name || i.menu_type_name || '').trim() || 'General') === selectedCategory);
    }
    if (dietFilter === 'VEG') {
      items = items.filter(i => {
        const ft = String(i.food_type || i.food_type_name || '').toLowerCase();
        if (ft) return ft.includes('veg') && !ft.includes('non');
        return i.is_veg === true;
      });
    } else if (dietFilter === 'NONVEG') {
      items = items.filter(i => {
        const ft = String(i.food_type || i.food_type_name || '').toLowerCase();
        if (ft) return ft.includes('non') || ft.includes('nv');
        return i.is_veg === false;
      });
    } else if (dietFilter === 'FAVORITES') {
      items = items.filter(i => favorites.has(i.id));
    } else if (dietFilter === 'BESTSELLER') {
      items = items.filter(i => i.is_bestseller === true);
    } else if (dietFilter === 'SPICY') {
      items = items.filter(i => i.is_spicy === true);
    } else if (dietFilter === 'JAIN') {
      items = items.filter(i => i.is_jain === true);
    }
    const q = String(search || '').trim().toLowerCase();
    if (q) {
      items = items.filter(i =>
        String(i.name || i.menu_item_name || '').toLowerCase().includes(q) ||
        String(i.sku || i.item_code || '').toLowerCase().includes(q) ||
        String(i.category_name || '').toLowerCase().includes(q)
      );
    }
    return items;
  }, [menuItems, selectedCategory, dietFilter, search, favorites]);

  const searchResults = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return [];
    return menuItems.filter(i =>
      String(i.name || i.menu_item_name || '').toLowerCase().includes(q) ||
      String(i.sku || i.item_code || '').toLowerCase().includes(q)
    ).slice(0, 8);
  }, [menuItems, search]);

  const tableName = serviceType === 'room-service'
    ? `Room ${roomInfo?.room_number || ''}`
    : serviceType === 'takeaway' ? 'Takeaway' : (tableInfo?.table_no || tableInfo?.table_name || 'Table');

  const itemName  = item => String(item.name || item.menu_item_name || item.menu_item_code || '').trim();
  const itemPrice = item => Number(item.base_price || item.rate || item.per_unit_price || item.effective_unit_price || 0);

  // Context bar computed values
  const seated      = humanDur(sessionInfo?.opened_at);
  const seatedStale = sessionInfo?.opened_at
    ? (Date.now() - new Date(sessionInfo.opened_at).getTime()) > 24 * 60 * 60 * 1000
    : false;
  const pax         = sessionInfo?.pax;
  const capacity    = tableInfo?.capacity;
  const serverName  = sessionInfo?.steward_name || existingOrders[0]?.created_by_username || '';
  const tableTotal  = existingOrders.reduce((s, o) => s + Number(o.order_total || 0), 0);
  const isRestaurant = serviceType === 'restaurant';

  // ─── Context Bar (top strip, dark navy) ──────────────────────────────────
  const ctxBar = (
    <div style={{
      background: 'linear-gradient(135deg, #1a1f4b 0%, #0f172a 100%)',
      color: '#fff',
      padding: '0 1rem',
      height: 52,
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? 8 : 16,
      flexShrink: 0,
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      position: 'sticky',
      top: 0,
      zIndex: 20,
    }}>
      {/* Back arrow */}
      <div
        onClick={onBack}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.1)', flexShrink: 0, transition: 'background 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
      >
        <span style={{ fontSize: '1rem' }}>&#8592;</span>
      </div>

      {/* Table name + icon */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
        <span style={{ fontSize: '1.1rem' }}>{isRestaurant ? '🪑' : serviceType === 'room-service' ? '🛏️' : '🥡'}</span>
        <span style={{ fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.01em' }}>{tableName}</span>
      </div>

      {isRestaurant && (
        <>
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)', flexShrink: 0 }} />

          {/* Occupied status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e', flexShrink: 0 }} />
            <span style={{ fontSize: '0.8rem', color: '#86efac', fontWeight: 600 }}>Occupied</span>
            {pax && capacity && (
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)' }}>({pax}/{capacity} Pax)</span>
            )}
          </div>

          {!isMobile && serverName && (
            <>
              <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)', flexShrink: 0 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                <span style={{ fontSize: '0.85rem' }}>&#128100;</span>
                <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.45)', marginRight: 3 }}>Server:</span>
                  <b style={{ color: '#fff' }}>{serverName}</b>
                </span>
              </div>
            </>
          )}

          {!isMobile && seated && (
            <>
              <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)', flexShrink: 0 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                <span style={{ fontSize: '0.85rem' }}>&#9203;</span>
                <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.45)', marginRight: 3 }}>Seated:</span>
                  <b style={{ color: seatedStale ? '#f87171' : '#fbbf24' }} title={seatedStale ? 'Stale session — may need to be closed' : undefined}>{seated}{seatedStale ? ' ⚠' : ''}</b>
                </span>
              </div>
            </>
          )}
          {onClearSession && sessionInfo?.id && (
            <button
              type="button"
              onClick={onClearSession}
              title="Close this table's session (e.g. a stuck table with no orders)"
              style={{
                padding: '3px 10px', borderRadius: 999, border: '1px solid rgba(248,113,113,0.5)',
                background: seatedStale ? 'rgba(248,113,113,0.18)' : 'rgba(255,255,255,0.08)',
                color: '#fca5a5', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0,
              }}
            >
              Clear Session
            </button>
          )}
        </>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Current total */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
        <span style={{ fontSize: '0.67rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {cartItems.length > 0 ? 'New Items' : 'Table Total'}
        </span>
        <span style={{ fontSize: '1rem', fontWeight: 700, color: cartItems.length > 0 ? '#34d399' : '#fff', letterSpacing: '-0.01em' }}>
          {currencySymbol}{Number(cartItems.length > 0 ? (cartTotal || cartSubtotal || 0) : tableTotal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </span>
      </div>
    </div>
  );

  // ─── Category row — horizontal pill/tile buttons, same treatment at every
  // screen size (icon on top, label below), matching the reference design. ──
  const categoryRow = (
    <div style={{ display: 'flex', gap: isMobile ? 6 : 10, overflowX: 'auto', paddingBottom: 6, marginBottom: '0.9rem' }}>
      {categories.map(cat => {
        const active = selectedCategory === cat;
        return (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              minWidth: isMobile ? 64 : 76,
              flexShrink: 0,
              padding: isMobile ? '0.5rem 0.4rem' : '0.65rem 0.5rem',
              borderRadius: 12,
              border: active ? '2px solid #16a34a' : '1px solid #e2e8f0',
              background: active ? '#f0fdf4' : '#fff',
              boxShadow: active ? '0 2px 8px rgba(22,163,74,0.18)' : '0 1px 3px rgba(0,0,0,0.05)',
              cursor: 'pointer',
              transition: 'box-shadow 0.15s, border-color 0.15s',
            }}
          >
            <span style={{ fontSize: isMobile ? '1.15rem' : '1.35rem', lineHeight: 1 }}>{catIcon(cat)}</span>
            <span style={{
              fontSize: isMobile ? '0.68rem' : '0.72rem',
              fontWeight: active ? 700 : 600,
              color: active ? '#15803d' : '#475569',
              textAlign: 'center',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}>
              {cat === 'ALL' ? 'All' : cat.length > 12 ? cat.slice(0, 11) + '…' : cat}
            </span>
          </button>
        );
      })}
    </div>
  );

  // ─── Menu Grid ─────────────────────────────────────────────────────────────
  const menuGrid = (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(auto-fill,minmax(160px,1fr))', gap: '0.85rem' }}>
      {filtered.map(item => {
        const cartItem = cartItems.find(ci => (ci.menu_item_id || ci.id) === item.id);
        const inCart = Boolean(cartItem);
        const qty = inCart ? Number(cartItem.qty ?? cartItem.quantity ?? 1) : (itemQuantities[item.id] || 1);
        const price = itemPrice(item);
        const note = cartItem?.special_instructions || cartItem?.remarks || '';
        const badge = foodBadge(item);

        return (
          <div
            key={item.id}
            style={{
              borderRadius: 16,
              border: inCart ? '2px solid #3b82f6' : '1px solid #e8ecf0',
              background: '#fff',
              overflow: 'hidden',
              boxShadow: inCart ? '0 0 0 4px rgba(59,130,246,0.1), 0 4px 16px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.07)',
              transition: 'box-shadow 0.18s, transform 0.18s, border-color 0.15s',
            }}
            onMouseEnter={e => { if (!inCart) { e.currentTarget.style.boxShadow = '0 8px 28px rgba(0,0,0,0.14)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
            onMouseLeave={e => { if (!inCart) { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'; e.currentTarget.style.transform = 'none'; } }}
          >
            {/* Image — square aspect ratio */}
            <div style={{ position: 'relative', paddingTop: '100%', background: 'linear-gradient(135deg,#f0f4f8,#e8ecf0)', overflow: 'hidden' }}>
              {item.image_url ? (
                <img
                  src={item.image_url}
                  alt=""
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', color: '#c7d2dc' }}>
                  {catIcon(String(item.category_name || ''))}
                </div>
              )}
              {/* Food type badge */}
              {badge && (
                <div style={{ position: 'absolute', top: 7, left: 7, background: 'rgba(255,255,255,0.95)', borderRadius: 5, padding: '2px 5px', backdropFilter: 'blur(6px)', boxShadow: '0 1px 4px rgba(0,0,0,0.12)', zIndex: 2 }}>
                  {badge}
                </div>
              )}
              {/* Bestseller ribbon */}
              {item.is_bestseller && (
                <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#fff', borderRadius: 5, padding: '2px 7px', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.02em', boxShadow: '0 2px 6px rgba(217,119,6,0.4)', zIndex: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                  ⭐ Bestseller
                </div>
              )}
              {/* Favorite heart — per-browser preference (localStorage), not a shared/synced setting */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }}
                title={favorites.has(item.id) ? 'Remove from favorites' : 'Add to favorites'}
                style={{ position: 'absolute', top: 7, right: 7, width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(6px)', boxShadow: '0 1px 4px rgba(0,0,0,0.14)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', zIndex: 2, padding: 0, lineHeight: 1 }}
              >
                {favorites.has(item.id) ? '❤️' : '🤍'}
              </button>
              {/* Circle "+" add button overlaid at bottom-right of image */}
              {!inCart && (
                <button
                  onClick={() => onAddToCart(item)}
                  style={{ position: 'absolute', bottom: 8, right: 8, width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'linear-gradient(135deg,#16a34a,#15803d)', color: '#fff', fontWeight: 700, fontSize: '1.3rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 3px 10px rgba(21,128,61,0.45)', transition: 'transform 0.12s', zIndex: 2, lineHeight: 1 }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                >+</button>
              )}
              {/* In-cart qty badge */}
              {inCart && (
                <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', borderRadius: 20, padding: '3px 10px', fontSize: '0.7rem', fontWeight: 700, boxShadow: '0 2px 6px rgba(37,99,235,0.35)', zIndex: 2 }}>
                  {qty}×
                </div>
              )}
              {/* Bottom gradient */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 40, background: 'linear-gradient(transparent, rgba(0,0,0,0.18))' }} />
            </div>

            {/* Card footer */}
            <div style={{ padding: '0.55rem 0.7rem 0.65rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0f172a', marginBottom: '0.28rem', lineHeight: 1.3, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{itemName(item)}</span>
                {item.is_spicy && <span title="Spicy" style={{ fontSize: '0.7rem', flexShrink: 0 }}>🌶️</span>}
                {item.is_jain && <span title="Jain" style={{ fontSize: '0.6rem', fontWeight: 700, color: '#059669', border: '1px solid #a7f3d0', borderRadius: 3, padding: '0 3px', flexShrink: 0 }}>JAIN</span>}
              </div>

              {/* Price row — always shown */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0d9488', letterSpacing: '-0.01em' }}>
                  {currencySymbol}{price}
                </span>

                {/* In-cart: inline − qty + controls */}
                {inCart && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <button onClick={() => onUpdateCartQty?.(item.id, -1)} style={{ width: 24, height: 24, borderRadius: '50%', border: 'none', background: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>−</button>
                    <span style={{ fontWeight: 700, minWidth: 16, textAlign: 'center', fontSize: '0.85rem' }}>{qty}</span>
                    <button onClick={() => onUpdateCartQty?.(item.id, 1)} style={{ width: 24, height: 24, borderRadius: '50%', border: 'none', background: '#dbeafe', color: '#2563eb', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>+</button>
                  </div>
                )}
              </div>

              {/* In-cart: Customize link + expanded options */}
              {inCart && (
                <div style={{ marginTop: '0.28rem' }}>
                  <div
                    onClick={() => setEditingNoteId(editingNoteId === item.id ? null : item.id)}
                    style={{ color: '#6366f1', fontSize: '0.69rem', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}
                  >
                    <span>{editingNoteId === item.id ? '▲' : '⚙'}</span>
                    <span>Customize</span>
                    {(note || cartItem?.spice_level || cartItem?.portion_size) && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#6366f1', display: 'inline-block' }} />}
                  </div>
                  {editingNoteId === item.id && (
                    <div style={{ marginTop: '0.3rem' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                        <select value={cartItem?.spice_level || ''} onChange={e => onUpdatePreference?.(item.id, 'spice_level', e.target.value)} style={{ padding: '0.18rem 0.25rem', borderRadius: 5, border: '1px solid #e2e8f0', fontSize: '0.67rem', color: '#374151', background: cartItem?.spice_level ? '#fef3c7' : '#f9fafb', cursor: 'pointer' }}>
                          <option value="">Spice</option>
                          <option value="MILD">Mild</option>
                          <option value="MEDIUM">Medium</option>
                          <option value="SPICY">Spicy</option>
                          <option value="EXTRA_SPICY">Extra Spicy</option>
                        </select>
                        <select value={cartItem?.portion_size || ''} onChange={e => onUpdatePreference?.(item.id, 'portion_size', e.target.value)} style={{ padding: '0.18rem 0.25rem', borderRadius: 5, border: '1px solid #e2e8f0', fontSize: '0.67rem', color: '#374151', background: cartItem?.portion_size ? '#dbeafe' : '#f9fafb', cursor: 'pointer' }}>
                          <option value="">Portion</option>
                          <option value="QUARTER">Quarter</option>
                          <option value="HALF">Half</option>
                          <option value="FULL">Full</option>
                        </select>
                      </div>
                      <input
                        type="text"
                        placeholder="e.g. no onion, extra spicy…"
                        value={note}
                        onChange={e => onUpdateNote(item.id, e.target.value)}
                        style={{ width: '100%', padding: '0.28rem 0.4rem', borderRadius: 6, border: '1px solid #c7d2fe', fontSize: '0.72rem', outline: 'none', background: '#fafafa', boxSizing: 'border-box' }}
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // ─── Order panel cart items ───────────────────────────────────────────────
  const cartSection = cartItems.length === 0 ? (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem 0', gap: 8, color: '#94a3b8' }}>
      <span style={{ fontSize: '2rem' }}>&#127869;</span>
      <span style={{ fontSize: '0.82rem' }}>No items added yet</span>
    </div>
  ) : cartItems.map(ci => {
    const ciName  = ci.menu_item_name || ci.name || '';
    const ciPrice = Number(ci.unit_price || ci.base_price || 0);
    const ciQty   = Number(ci.qty || ci.quantity_servings || 1);
    const ciId    = ci.menu_item_id || ci.id;
    const ciNote  = ci.special_instructions || ci.remarks || '';
    return (
      <div key={ciId} style={{ marginBottom: '0.6rem', paddingBottom: '0.6rem', borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
            <span style={{ marginTop: 2 }}>{foodBadge(ci)}</span>
            <span style={{ fontWeight: 600, fontSize: '0.83rem', color: '#111827', lineHeight: 1.3 }}>{ciName}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0d9488' }}>
              {currencySymbol}{(ciPrice * ciQty).toFixed(0)}
            </span>
            <span
              onClick={() => onRemoveFromCart(ciId)}
              style={{ width: 18, height: 18, borderRadius: '50%', background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
            >&#10005;</span>
          </div>
        </div>
        <div style={{ color: '#94a3b8', fontSize: '0.73rem', marginTop: 2 }}>
          {currencySymbol}{ciPrice} × {ciQty}
        </div>
        {(ci.spice_level || ci.portion_size) && (
          <div style={{ display: 'flex', gap: '0.3rem', marginTop: 3, flexWrap: 'wrap' }}>
            {ci.spice_level && (
              <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4, background: ci.spice_level === 'MILD' ? '#dcfce7' : ci.spice_level === 'MEDIUM' ? '#fef3c7' : '#fee2e2', color: ci.spice_level === 'MILD' ? '#15803d' : ci.spice_level === 'MEDIUM' ? '#92400e' : '#991b1b', fontWeight: 600 }}>
                {ci.spice_level === 'EXTRA_SPICY' ? 'Extra Spicy' : ci.spice_level.charAt(0) + ci.spice_level.slice(1).toLowerCase()}
              </span>
            )}
            {ci.portion_size && (
              <span style={{ fontSize: '0.65rem', padding: '1px 6px', borderRadius: 4, background: '#dbeafe', color: '#1d4ed8', fontWeight: 600 }}>
                {ci.portion_size.charAt(0) + ci.portion_size.slice(1).toLowerCase()}
              </span>
            )}
          </div>
        )}
        {ciNote && <div style={{ color: '#6366f1', fontSize: '0.72rem', marginTop: 2, fontStyle: 'italic' }}>📝 {ciNote}</div>}
      </div>
    );
  });

  // ─── Existing active orders section ──────────────────────────────────────
  const existingOrdersSection = existingOrders.length > 0 && (
    <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: cartItems.length > 0 ? '1px dashed #e2e8f0' : 'none' }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
        Active Orders
      </div>
      {existingOrders.map(o => {
        const items = orderItemsById[o.id];
        const [bg, clr, lbl] = ORDER_STATUS[o.status] || ['#fef3c7', '#92400e', o.status];
        return (
          <div key={o.id} style={{ marginBottom: '0.5rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.5rem 0.65rem', fontSize: '0.78rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
              <span style={{ fontWeight: 700, color: '#1e3a5f', fontSize: '0.8rem' }}>Order #{o.order_no}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontWeight: 700, color: '#111827' }}>{currencySymbol}{Number(o.order_total || 0).toFixed(0)}</span>
                <span style={{ padding: '1px 6px', borderRadius: 4, background: bg, color: clr, fontWeight: 600, fontSize: '0.65rem' }}>{lbl}</span>
              </div>
            </div>
            {(o.opened_at || o.order_date) && (
              <div style={{ color: '#94a3b8', fontSize: '0.7rem', marginBottom: 2 }}>
                🕒 {relTime(o.opened_at || o.order_date)}
              </div>
            )}
            {items === undefined ? (
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>Loading…</div>
            ) : items.length === 0 ? null : (
              <ul style={{ margin: '4px 0 0', padding: '4px 0 0', listStyle: 'none', borderTop: '1px dashed #e2e8f0' }}>
                {items.map(it => (
                  <li key={it.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: '0.73rem', color: '#374151' }}>
                    <span style={{ flex: 1 }}>
                      <b>{Number(it.qty || 0)}×</b> {it.menu_item_name || `Item`}
                      {it.remarks ? <span style={{ color: '#94a3b8', fontStyle: 'italic' }}> — {it.remarks}</span> : null}
                    </span>
                    <span style={{ fontWeight: 600 }}>{currencySymbol}{Number(it.line_total || (Number(it.qty || 0) * Number(it.unit_price || 0))).toFixed(0)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );

  // ─── Order Panel (right sidebar) ─────────────────────────────────────────
  const orderPanel = (
    <div style={{
      width: isMobile ? '100%' : 300,
      minWidth: isMobile ? undefined : 280,
      background: '#fff',
      borderLeft: isMobile ? 'none' : '1px solid #e2e8f0',
      display: 'flex',
      flexDirection: 'column',
      position: isMobile ? 'fixed' : 'sticky',
      top: isMobile ? undefined : 52,
      bottom: isMobile ? 0 : undefined,
      right: 0,
      height: isMobile ? undefined : 'calc(100vh - 120px)',
      zIndex: isMobile ? 30 : 1,
      boxShadow: isMobile ? '0 -4px 20px rgba(0,0,0,0.12)' : 'none',
    }}>
      {/* Panel header */}
      <div style={{ padding: '0.9rem 1rem 0.7rem', background: 'linear-gradient(135deg, #16a34a, #15803d)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Current Order</div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>{tableName}</div>
          </div>
          <div style={{ background: cartItems.length > 0 ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 700, color: '#fff' }}>
            {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
          </div>
        </div>
      </div>

      {/* Scrollable cart + existing orders */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '0.75rem 1rem' }}>
        {cartSection}
        {existingOrdersSection}
      </div>

      {/* Totals + actions */}
      <div style={{ padding: '0.75rem 1rem 0.85rem', borderTop: '1px solid #e2e8f0', flexShrink: 0 }}>

        {/* Guest Details / Apply Offer row */}
        {(onGuestDetails || onDiscountValueChange) && (
          <div style={{ display: 'flex', gap: 6, marginBottom: '0.6rem' }}>
            {onGuestDetails && (
              <button type="button" onClick={onGuestDetails}
                style={{ flex: 1, padding: '0.42rem', borderRadius: 8, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
              >&#128100; Guest Details</button>
            )}
            {onDiscountValueChange && (
              <button type="button" onClick={() => setShowOfferEditor(v => !v)}
                style={{ flex: 1, padding: '0.42rem', borderRadius: 8, border: '1px solid #bbf7d0', background: discountAmount > 0 ? '#dcfce7' : '#f0fdf4', color: '#15803d', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
              >&#127991;&#65039; {discountAmount > 0 ? `Offer: −${currencySymbol}${discountAmount.toFixed(0)}` : 'Apply Offer'}</button>
            )}
          </div>
        )}

        {/* Inline offer editor */}
        {showOfferEditor && onDiscountValueChange && (
          <div style={{ display: 'flex', gap: 5, marginBottom: '0.6rem', padding: '0.5rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
            <select value={discountType} onChange={e => onDiscountTypeChange?.(e.target.value)} style={{ padding: '0.3rem', borderRadius: 6, border: '1px solid #bbf7d0', fontSize: '0.78rem', background: '#fff' }}>
              <option value="PERCENT">%</option>
              <option value="FIXED">{currencySymbol}</option>
            </select>
            <input type="number" min="0" placeholder="0" value={discountValue}
              onChange={e => onDiscountValueChange?.(e.target.value)}
              style={{ flex: 1, minWidth: 0, padding: '0.3rem 0.5rem', borderRadius: 6, border: '1px solid #bbf7d0', fontSize: '0.82rem', outline: 'none' }} />
            <button type="button" onClick={() => setShowOfferEditor(false)}
              style={{ padding: '0.3rem 0.65rem', borderRadius: 6, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: '0.76rem', cursor: 'pointer' }}
            >Apply</button>
          </div>
        )}

        {/* Subtotal / Discount / Tax / Total */}
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#64748b', marginBottom: 3 }}>
            <span>Subtotal</span>
            <span>{currencySymbol}{Number(cartSubtotal || 0).toFixed(2)}</span>
          </div>
          {discountAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#16a34a', marginBottom: 3 }}>
              <span>Offer discount</span>
              <span>−{currencySymbol}{discountAmount.toFixed(2)}</span>
            </div>
          )}
          {taxPercent != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#94a3b8', marginBottom: 3 }}>
              <span>Tax ({taxPercent}%)</span>
              <span>{currencySymbol}{Number(cartTaxAmount || 0).toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 700, color: '#111827', paddingTop: 5, borderTop: '1px solid #f1f5f9', marginTop: 3 }}>
            <span>Grand Total</span>
            <span style={{ color: '#0d9488' }}>{currencySymbol}{Number(cartTotal ?? cartSubtotal ?? 0).toFixed(2)}</span>
          </div>
        </div>

        {/* Clear + Place Order */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          {onClearCart && (
            <button type="button"
              onClick={() => { if (cartItems.length > 0 && window.confirm('Clear all items from this order?')) onClearCart(); }}
              disabled={cartItems.length === 0}
              title="Clear cart"
              style={{ padding: '0 0.9rem', borderRadius: 10, border: '1px solid #fecaca', background: cartItems.length === 0 ? '#f8fafc' : '#fef2f2', color: cartItems.length === 0 ? '#cbd5e1' : '#dc2626', fontWeight: 700, fontSize: '0.95rem', cursor: cartItems.length === 0 ? 'default' : 'pointer', flexShrink: 0 }}
            >&#128465;&#65039;</button>
          )}
          <button
            onClick={onPlaceOrder}
            disabled={cartItems.length === 0 || placing}
            style={{
              flex: 1,
              padding: '0.65rem',
              borderRadius: 10,
              border: 'none',
              background: cartItems.length === 0
                ? '#e2e8f0'
                : 'linear-gradient(135deg, #16a34a, #15803d)',
              color: cartItems.length === 0 ? '#94a3b8' : '#fff',
              fontWeight: 700,
              fontSize: '0.88rem',
              cursor: cartItems.length === 0 ? 'default' : 'pointer',
              opacity: placing ? 0.65 : 1,
              transition: 'opacity 0.15s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            {placing ? '⏳ Sending…' : cartItems.length > 0 ? '✓ Place Order' : 'Add items to place order'}
          </button>
        </div>

        {/* Bottom quick-action toolbar */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${onSaveOrder && onPrintLatestKot ? 4 : onSaveOrder || onPrintLatestKot ? 3 : 2}, 1fr)`, gap: 5 }}>
          <button type="button" onClick={onPlaceOrder} disabled={cartItems.length === 0 || placing} title="Send cart to kitchen"
            style={{ padding: '0.45rem 0.25rem', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: cartItems.length === 0 ? '#cbd5e1' : '#0f172a', fontWeight: 700, fontSize: '0.7rem', cursor: cartItems.length === 0 ? 'default' : 'pointer' }}
          >&#127859; KOT</button>
          {onSaveOrder && (
            <button type="button" onClick={onSaveOrder} disabled={cartItems.length === 0 || savingOrder || placing} title="Save the order without notifying the kitchen yet"
              style={{ padding: '0.45rem 0.25rem', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: cartItems.length === 0 ? '#cbd5e1' : '#0f172a', fontWeight: 700, fontSize: '0.7rem', cursor: cartItems.length === 0 ? 'default' : 'pointer' }}
            >{savingOrder ? '⏳' : '\u{1F4BE}'} Save</button>
          )}
          {onPrintLatestKot && (
            <button type="button" onClick={onPrintLatestKot} title="Reprint the latest KOT for this table"
              style={{ padding: '0.45rem 0.25rem', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a', fontWeight: 700, fontSize: '0.7rem', cursor: 'pointer' }}
            >&#128424;&#65039; Print</button>
          )}
          {(onGenerateBill || onTableTransfer || onExit) && existingOrders.length > 0 && (
            <div style={{ position: 'relative' }}>
              <button type="button" onClick={() => setShowMoreActions(v => !v)}
                style={{ width: '100%', padding: '0.45rem 0.25rem', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a', fontWeight: 700, fontSize: '0.7rem', cursor: 'pointer' }}
              >&#8942; More</button>
              {showMoreActions && (
                <div style={{ position: 'absolute', bottom: '115%', right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 170, zIndex: 30, overflow: 'hidden' }}>
                  {onGenerateBill && (
                    <button type="button" onClick={() => { setShowMoreActions(false); onGenerateBill(); }}
                      style={{ width: '100%', textAlign: 'left', padding: '0.55rem 0.75rem', border: 'none', background: '#fff', fontSize: '0.8rem', fontWeight: 600, color: '#15803d', cursor: 'pointer' }}
                    >&#9654; Finalize Bill</button>
                  )}
                  {onTableTransfer && (
                    <button type="button" onClick={() => { setShowMoreActions(false); setShowTransferPicker(true); setTransferTarget(''); }}
                      style={{ width: '100%', textAlign: 'left', padding: '0.55rem 0.75rem', border: 'none', borderTop: '1px solid #f1f5f9', background: '#fff', fontSize: '0.8rem', fontWeight: 600, color: '#7c3aed', cursor: 'pointer' }}
                    >&#8644; Transfer Table</button>
                  )}
                  {onExit && (
                    <button type="button" onClick={() => { setShowMoreActions(false); onExit(); }}
                      style={{ width: '100%', textAlign: 'left', padding: '0.55rem 0.75rem', border: 'none', borderTop: '1px solid #f1f5f9', background: '#fff', fontSize: '0.8rem', fontWeight: 600, color: '#64748b', cursor: 'pointer' }}
                    >&#8617; Exit to Tables</button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Inline table transfer picker — opened from More Actions */}
        {showTransferPicker && (
          <div style={{ marginTop: 8, padding: '0.6rem', background: '#faf5ff', border: '1px solid #ddd6fe', borderRadius: 8 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6d28d9', marginBottom: '0.35rem' }}>Transfer to Table</div>
            <select
              value={transferTarget}
              onChange={e => setTransferTarget(e.target.value)}
              style={{ width: '100%', padding: '0.38rem 0.45rem', border: '1px solid #ddd6fe', borderRadius: 6, fontSize: '0.82rem', marginBottom: '0.35rem', outline: 'none', background: '#fff' }}
            >
              <option value="">— Select Table —</option>
              {tables.filter(t => t.is_active !== false && String(t.id) !== String(tableInfo?.id)).map(t => (
                <option key={t.id} value={t.id}>{t.table_no || t.table_name}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              <button
                onClick={() => { if (transferTarget && onTableTransfer) { onTableTransfer(transferTarget); setShowTransferPicker(false); setTransferTarget(''); } }}
                disabled={!transferTarget}
                style={{ flex: 1, padding: '0.38rem', borderRadius: 6, border: 'none', background: transferTarget ? '#7c3aed' : '#d1d5db', color: '#fff', fontWeight: 700, fontSize: '0.76rem', cursor: transferTarget ? 'pointer' : 'not-allowed' }}
              >Confirm</button>
              <button
                onClick={() => { setShowTransferPicker(false); setTransferTarget(''); }}
                style={{ padding: '0.38rem 0.6rem', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#6b7280', fontSize: '0.76rem', cursor: 'pointer' }}
              >Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f4f6fb' }}>

      {/* Context bar */}
      {ctxBar}

      {/* Main row: menu + order panel */}
      <div style={{ display: 'flex', flex: 1, alignItems: 'flex-start' }}>

        {/* Menu content */}
        <div style={{ flex: 1, padding: isMobile ? '0.75rem' : '1rem 1.25rem', minWidth: 0 }}>

          {/* Search */}
          <div style={{ position: 'relative', marginBottom: '0.9rem' }} ref={dropdownRef}>
            <input
              ref={searchRef}
              type="text"
              placeholder="Find your favorites…"
              value={search}
              onChange={e => { setSearch(e.target.value); setShowDropdown(true); }}
              onFocus={() => search && setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
              style={{
                width: '100%',
                padding: '0.58rem 0.75rem 0.58rem 2.3rem',
                borderRadius: 10,
                border: '2px solid #e2e8f0',
                fontSize: '0.9rem',
                outline: 'none',
                background: '#fff',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s',
              }}
              onFocusCapture={e => { e.target.style.borderColor = '#3b82f6'; }}
              onBlurCapture={e => { e.target.style.borderColor = '#e2e8f0'; }}
            />
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '1rem' }}>&#128269;</span>

            {showDropdown && searchResults.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 25, background: '#fff', borderRadius: '0 0 12px 12px', border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 320, overflowY: 'auto' }}>
                {searchResults.map(item => (
                  <div
                    key={item.id}
                    onClick={() => { onAddToCart(item); setSearch(''); setShowDropdown(false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.55rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                  >
                    {item.image_url && <img src={item.image_url} alt="" style={{ width: 38, height: 38, borderRadius: 7, objectFit: 'cover' }} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.87rem', display: 'flex', alignItems: 'center', gap: 5 }}>{itemName(item)} {foodBadge(item)}</div>
                      <div style={{ color: '#94a3b8', fontSize: '0.73rem' }}>{String(item.category_name || '').trim()}</div>
                    </div>
                    <div style={{ fontWeight: 700, color: '#0d9488', fontSize: '0.88rem' }}>{currencySymbol}{itemPrice(item)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Category row */}
          {categoryRow}

          {/* Diet filter row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {[['ALL', 'All', null], ['VEG', 'Veg', VEG_DOT], ['NONVEG', 'Non-Veg', NV_DOT], ['BESTSELLER', 'Bestseller', '⭐'], ['SPICY', 'Spicy', '🌶️'], ['JAIN', 'Jain', null], ['FAVORITES', 'Favorites', '❤️']].map(([key, label, icon]) => (
                <button
                  key={key}
                  onClick={() => setDietFilter(key)}
                  style={{
                    padding: '0.38rem 0.85rem',
                    border: dietFilter === key ? '1px solid #1e293b' : '1px solid #e2e8f0',
                    borderRadius: 9,
                    background: dietFilter === key ? '#1e293b' : '#fff',
                    color: dietFilter === key ? '#fff' : '#64748b',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    transition: 'background 0.15s',
                  }}
                >
                  {icon && <span style={{ filter: dietFilter === key ? 'brightness(2)' : 'none' }}>{icon}</span>}
                  {label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              {filtered.length} item{filtered.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Menu grid */}
          {menuGrid}

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '3rem 0', fontSize: '0.9rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>🔍</div>
              No items found
            </div>
          )}

          {/* Bottom padding so last row not hidden behind mobile order panel */}
          {isMobile && <div style={{ height: 120 }} />}
        </div>

        {/* Order panel */}
        {orderPanel}
      </div>
    </div>
  );
}
